import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import useAuth from '../hooks/useAuth';
import { ApiClientError, api } from '../services/api';
import { realtime } from '../services/realtime';
import type {
  AccountSummary,
  AppErrorState,
  CallRecord,
  Chat,
  ChatUpdateInput,
  Message,
  Settings,
  SystemStatus,
} from '../types';
import { ACTIVE_CALL_STATES } from '../types';

export type ChatContextValue = {
  chats: Chat[];
  activeChat: Chat | null;
  messages: Message[];
  settings: Settings | null;
  features: Record<string, boolean> | null;
  systemStatus: SystemStatus | null;
  accounts: AccountSummary[];
  activeCalls: CallRecord[];
  loading: boolean;
  loadingMessages: boolean;
  offline: boolean;
  realtimeConnected: boolean;
  error: AppErrorState | null;
  selectChat: (chatId: string | null) => void;
  sendMessage: (content: string, replyToMessageId?: string) => Promise<void>;
  createChat: (title: string, phone?: string) => Promise<Chat>;
  updateChat: (update: ChatUpdateInput) => Promise<void>;
  updateChatById: (chatId: string, update: ChatUpdateInput) => Promise<Chat>;
  deleteChat: (chatId: string) => Promise<void>;
  markChatRead: (chatId: string) => Promise<void>;
  toggleFavorite: (chatId: string) => Promise<void>;
  setGlobalAutomode: (enabled: boolean) => Promise<void>;
  setFeatures: (features: Record<string, boolean>) => void;
  clearError: () => void;
  reload: () => Promise<void>;
};

export const ChatContext = createContext<ChatContextValue | null>(null);

const sortChats = (chats: Chat[]): Chat[] =>
  [...chats].sort((left, right) => {
    // Primary: last activity descending (newest first).
    // Uses activityAt which considers lastInboundAt, lastOutboundAt, and
    // updatedAt — ensuring both inbound and outbound messages bring the
    // chat to the top of the list.
    const leftTime = Math.max(
      left.lastInboundAt ? Date.parse(left.lastInboundAt) : 0,
      left.lastOutboundAt ? Date.parse(left.lastOutboundAt) : 0,
      Date.parse(left.updatedAt),
    );
    const rightTime = Math.max(
      right.lastInboundAt ? Date.parse(right.lastInboundAt) : 0,
      right.lastOutboundAt ? Date.parse(right.lastOutboundAt) : 0,
      Date.parse(right.updatedAt),
    );
    if (leftTime !== rightTime) return rightTime - leftTime;
    // Secondary: stable key by id (deterministic order for chats that share
    // the same timestamp — common during sync batches).
    return left.id.localeCompare(right.id);
  });

const computeUnreadCount = (
  messages: Message[] | undefined,
  lastReadAt: string | undefined,
): number => {
  if (!messages || messages.length === 0) return lastReadAt ? 0 : 1;
  return messages.filter(
    (message) =>
      message.direction === 'inbound' &&
      (!lastReadAt || Date.parse(message.createdAt) > Date.parse(lastReadAt)),
  ).length;
};

const mergeMessages = (current: Message[], incoming: Message): Message[] => {
  const index = current.findIndex(
    (message) =>
      message.id === incoming.id ||
      (incoming.clientMessageId &&
        message.clientMessageId === incoming.clientMessageId),
  );
  const next =
    index === -1
      ? [...current, incoming]
      : current.map((message, candidateIndex) =>
          candidateIndex === index ? incoming : message,
        );
  return next.sort(
    (left, right) => Date.parse(left.createdAt) - Date.parse(right.createdAt),
  );
};

const normalizeError = (reason: unknown): AppErrorState => ({
  code: reason instanceof ApiClientError ? reason.code : 'UNEXPECTED_ERROR',
  message:
    reason instanceof ApiClientError
      ? reason.message
      : 'Er is een onverwachte fout opgetreden.',
});

export const ChatProvider = ({ children }: { children: ReactNode }) => {
  const { state: authState } = useAuth();
  const [chats, setChats] = useState<Chat[]>([]);
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
  const [messagesByChat, setMessagesByChat] = useState<Record<string, Message[]>>(
    {},
  );

  useEffect(() => {
    messagesByChatRef.current = messagesByChat;
  }, [messagesByChat]);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [features, setFeatures] = useState<Record<string, boolean> | null>(null);
  const [systemStatus, setSystemStatus] = useState<SystemStatus | null>(null);
  const [accounts, setAccounts] = useState<AccountSummary[]>([]);
  const [activeCalls, setActiveCalls] = useState<CallRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [offline, setOffline] = useState(!navigator.onLine);
  const [realtimeConnected, setRealtimeConnected] = useState(false);
  const [error, setError] = useState<AppErrorState | null>(null);
  const messageRequest = useRef<AbortController | null>(null);
  const messagesByChatRef = useRef<Record<string, Message[]>>({});
  // Track previous WhatsApp connector state and active account ID so we can
  // detect transitions (→ ready, account switch) and refetch chats.
  const prevWaStateRef = useRef<string | null>(null);
  const prevActiveAccountIdRef = useRef<string | null>(null);

  const loadDashboard = useCallback(async (signal?: AbortSignal) => {
    const [nextChats, nextSettings, nextStatus, nextAccounts, nextCalls] = await Promise.all([
      api.chats(signal),
      api.settings(signal),
      api.status(signal),
      api.accounts(signal),
      api.activeCalls(signal),
    ]);
    setChats(sortChats(nextChats));
    setSettings(nextSettings);
    setSystemStatus(nextStatus);
    setAccounts(nextAccounts);
    setActiveCalls(nextCalls);
    setError(null);
    // Seed the transition-tracking refs from the initial load so we only
    // refetch on actual state transitions, not on the first load.
    prevWaStateRef.current = nextStatus?.whatsapp.state ?? null;
    const initialActive = nextAccounts.find((a) => a.isActive);
    prevActiveAccountIdRef.current = initialActive?.id ?? null;
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      await loadDashboard();
    } catch (reason) {
      setError(normalizeError(reason));
    } finally {
      setLoading(false);
    }
  }, [loadDashboard]);

  useEffect(() => {
    const updateOnline = () => setOffline(!navigator.onLine);
    window.addEventListener('online', updateOnline);
    window.addEventListener('offline', updateOnline);
    return () => {
      window.removeEventListener('online', updateOnline);
      window.removeEventListener('offline', updateOnline);
    };
  }, []);

  useEffect(() => {
    if (authState !== 'authenticated') {
      realtime.disconnect();
      messageRequest.current?.abort();
      setChats([]);
      setMessagesByChat({});
      setSelectedChatId(null);
      setSettings(null);
      setSystemStatus(null);
      setAccounts([]);
      setActiveCalls([]);
      setRealtimeConnected(false);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    void loadDashboard(controller.signal)
      .catch((reason: unknown) => {
        if (!controller.signal.aborted) setError(normalizeError(reason));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    const unsubscribe = realtime.subscribe({
      onChat: (incoming) => {
        setChats((current) => {
          if (incoming.deletedAt) {
            return sortChats(current.filter((chat) => chat.id !== incoming.id));
          }
          const existing = current.find((chat) => chat.id === incoming.id);
          const messages = messagesByChatRef.current[incoming.id];
          const unreadCount = messages
            ? computeUnreadCount(messages, incoming.lastReadAt)
            : existing
              ? existing.unreadCount
              : incoming.lastReadAt
                ? 0
                : 1;

          if (existing) {
            return sortChats(
              current.map((chat) =>
                chat.id === incoming.id
                  ? {
                      ...incoming,
                      lastMessage: chat.lastMessage,
                      unreadCount:
                        incoming.lastReadAt !== undefined && messages
                          ? computeUnreadCount(messages, incoming.lastReadAt)
                          : chat.unreadCount,
                    }
                  : chat,
              ),
            );
          }

          const next: Chat = {
            ...incoming,
            lastMessage: messages?.at(-1) ?? null,
            unreadCount,
          };
          return sortChats([next, ...current]);
        });
      },
      onMessage: (incoming) => {
        setMessagesByChat((current) => {
          const nextMessages = mergeMessages(current[incoming.chatId] ?? [], incoming);
          messagesByChatRef.current[incoming.chatId] = nextMessages;
          const next = { ...current, [incoming.chatId]: nextMessages };
          setChats((chatsCurrent) =>
            sortChats(
              chatsCurrent.map((chat) => {
                if (chat.id !== incoming.chatId) return chat;
                const updated: Chat = {
                  ...chat,
                  lastMessage: incoming,
                  updatedAt: incoming.updatedAt,
                };
                updated.unreadCount = computeUnreadCount(nextMessages, chat.lastReadAt);
                return updated;
              }),
            ),
          );
          return next;
        });
      },
      onStatus: (whatsapp) => {
        setSystemStatus((current) =>
          current ? { ...current, whatsapp } : current,
        );
        // When the connector transitions to 'ready' (after QR scan or Cloud
        // connect), refetch chats from the server. The backend syncs chats
        // on ready, so the server now has the fresh list — but the client's
        // in-memory list is stale from before connect.
        const prevState = prevWaStateRef.current;
        prevWaStateRef.current = whatsapp.state;
        if (whatsapp.state === 'ready' && prevState !== 'ready') {
          void api.chats()
            .then((fresh) => setChats(sortChats(fresh)))
            .catch((reason) => {
              console.error('[chat-context] refetch on ready failed', reason);
            });
        }
      },
      onAccounts: (incoming) => {
        setAccounts(incoming);
        // When the active account changes (user switches account in sidebar),
        // refetch chats scoped to the new active account.
        const newActive = incoming.find((a) => a.isActive);
        if (
          newActive &&
          prevActiveAccountIdRef.current !== newActive.id
        ) {
          prevActiveAccountIdRef.current = newActive.id;
          void api.chats()
            .then((fresh) => setChats(sortChats(fresh)))
            .catch((reason) => {
              console.error('[chat-context] refetch on account switch failed', reason);
            });
        }
      },
      onSettings: (incoming) => {
        setSettings(incoming);
      },
      onFeatures: (incoming) => {
        setFeatures(incoming);
      },
      onCall: (call) => {
        setActiveCalls((current) => {
          const isActive = ACTIVE_CALL_STATES.has(call.status);
          const matchById = (candidate: CallRecord) => candidate.id === call.id;
          const matchByProvider = (candidate: CallRecord) =>
            call.providerCallId !== undefined &&
            candidate.providerCallId !== undefined &&
            candidate.providerCallId === call.providerCallId;
          const index = current.findIndex(
            (candidate) => matchById(candidate) || matchByProvider(candidate),
          );
          if (!isActive) {
            return index === -1 ? current : current.filter((_, i) => i !== index);
          }
          if (index !== -1) {
            return current.map((candidate, i) =>
              i === index ? call : candidate,
            );
          }
          return [...current, call];
        });
      },
      onAvatar: () => undefined,
      onConnection: (connected) => {
        setRealtimeConnected(connected);
        // When the realtime socket reconnects after a server restart or
        // network interruption, the client's in-memory state is stale.
        // Reload all dashboard data so the UI is immediately in sync
        // without requiring a manual browser refresh.
        if (connected) {
          void loadDashboard().catch((reason) => {
            console.error('[chat-context] reload on reconnect failed', reason);
          });
        }
      },
    });
    return () => {
      controller.abort();
      messageRequest.current?.abort();
      unsubscribe();
    };
  }, [authState, loadDashboard]);

  const markChatRead = useCallback(async (chatId: string) => {
    try {
      const chat = await api.markChatRead(chatId);
      setChats((current) =>
        current.map((candidate) =>
          candidate.id === chat.id
            ? { ...chat, lastMessage: candidate.lastMessage, unreadCount: 0 }
            : candidate,
        ),
      );
    } catch {
      // non-critical: unread badge will sync on next reload
    }
  }, []);

  const selectChat = useCallback((chatId: string | null) => {
    messageRequest.current?.abort();
    setSelectedChatId(chatId);
    if (!chatId) return;
    void markChatRead(chatId);
    const controller = new AbortController();
    messageRequest.current = controller;
    setLoadingMessages(true);
    void api
      .messages(chatId, controller.signal)
      .then((messages) => {
        setMessagesByChat((current) => ({ ...current, [chatId]: messages }));
        setError(null);
      })
      .catch((reason: unknown) => {
        if (!controller.signal.aborted) setError(normalizeError(reason));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingMessages(false);
      });
  }, [markChatRead]);

  const sendMessage = useCallback(
    async (content: string, replyToMessageId?: string) => {
      if (!selectedChatId) return;
      const clientMessageId = crypto.randomUUID();
      const now = new Date().toISOString();
      const optimistic: Message = {
        id: `optimistic:${clientMessageId}`,
        chatId: selectedChatId,
        clientMessageId,
        direction: 'outbound',
        sender: 'agent',
        content,
        status: 'pending',
        createdAt: now,
        updatedAt: now,
        ...(replyToMessageId ? { replyToMessageId } : {}),
      };
      setMessagesByChat((current) => ({
        ...current,
        [selectedChatId]: mergeMessages(
          current[selectedChatId] ?? [],
          optimistic,
        ),
      }));
      try {
        const saved = await api.sendMessage(selectedChatId, {
          content,
          clientMessageId,
          ...(replyToMessageId ? { replyToMessageId } : {}),
        });
        setMessagesByChat((current) => ({
          ...current,
          [selectedChatId]: mergeMessages(current[selectedChatId] ?? [], saved),
        }));
        setError(null);
      } catch (reason) {
        setMessagesByChat((current) => ({
          ...current,
          [selectedChatId]: (current[selectedChatId] ?? []).map((message) =>
            message.clientMessageId === clientMessageId
              ? {
                  ...message,
                  status: 'failed',
                  errorCode:
                    reason instanceof ApiClientError
                      ? reason.code
                      : 'SEND_FAILED',
                }
              : message,
          ),
        }));
        setError(normalizeError(reason));
        throw reason;
      }
    },
    [selectedChatId],
  );

  const createChat = useCallback(async (title: string, phone?: string) => {
    const chat = await api.createChat({
      title,
      ...(phone ? { phone } : {}),
    });
    setChats((current) => sortChats([chat, ...current.filter((item) => item.id !== chat.id)]));
    setError(null);
    return chat;
  }, []);

  const updateChatById = useCallback(
    async (chatId: string, update: ChatUpdateInput) => {
      const chat = await api.updateChat(chatId, update);
      setChats((current) =>
        sortChats(
          current.map((candidate) =>
            candidate.id === chat.id
              ? { ...chat, lastMessage: candidate.lastMessage }
              : candidate,
          ),
        ),
      );
      setError(null);
      return chat;
    },
    [],
  );

  const updateChat = useCallback(
    async (update: ChatUpdateInput) => {
      if (!selectedChatId) return;
      await updateChatById(selectedChatId, update);
    },
    [selectedChatId, updateChatById],
  );

  const deleteChat = useCallback(async (chatId: string) => {
    await api.deleteChat(chatId);
    setChats((current) => sortChats(current.filter((candidate) => candidate.id !== chatId)));
    if (selectedChatId === chatId) {
      setSelectedChatId(null);
    }
    setError(null);
  }, [selectedChatId]);

  const toggleFavorite = useCallback(async (chatId: string) => {
    setChats((current) =>
      current.map((candidate) =>
        candidate.id === chatId
          ? { ...candidate, favorite: !candidate.favorite }
          : candidate,
      ),
    );
    try {
      const chat = await api.updateChat(chatId, {
        favorite: !chats.find((c) => c.id === chatId)?.favorite,
      });
      setChats((current) =>
        current.map((candidate) =>
          candidate.id === chat.id
            ? { ...chat, lastMessage: candidate.lastMessage }
            : candidate,
        ),
      );
    } catch {
      // revert on error
      setChats((current) =>
        current.map((candidate) =>
          candidate.id === chatId
            ? { ...candidate, favorite: !candidate.favorite }
            : candidate,
        ),
      );
    }
  }, [chats]);

  const setGlobalAutomode = useCallback(async (enabled: boolean) => {
    try {
      const next = await api.updateSettings(enabled);
      setSettings(next);
      setError(null);
    } catch (reason) {
      setError(normalizeError(reason));
      throw reason;
    }
  }, []);

  const activeChat =
    chats.find((candidate) => candidate.id === selectedChatId) ?? null;
  const messages = useMemo(
    () => (selectedChatId ? (messagesByChat[selectedChatId] ?? []) : []),
    [messagesByChat, selectedChatId],
  );
  const value = useMemo<ChatContextValue>(
    () => ({
      chats,
      activeChat,
      messages,
      settings,
      features,
      systemStatus,
      accounts,
      activeCalls,
      loading,
      loadingMessages,
      offline,
      realtimeConnected,
      error,
      selectChat,
      sendMessage,
      createChat,
      updateChat,
      updateChatById,
      deleteChat,
      markChatRead,
      toggleFavorite,
      setGlobalAutomode,
      setFeatures,
      clearError: () => setError(null),
      reload,
    }),
    [
      accounts,
      activeCalls,
      activeChat,
      chats,
      createChat,
      deleteChat,
      error,
      features,
      loading,
      loadingMessages,
      markChatRead,
      messages,
      offline,
      realtimeConnected,
      reload,
      selectChat,
      sendMessage,
      setFeatures,
      setGlobalAutomode,
      settings,
      systemStatus,
      toggleFavorite,
      updateChat,
      updateChatById,
    ],
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};